// @ts-nocheck
import { useEffect, useRef, useState } from "react";
import { CheckCircle, PackageSearch, ScanLine, XCircle } from "lucide-react";
import { request } from "./FunctionalApp";

async function api(path: string, init: RequestInit = {}) {
  const r = await request(path, init);
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(String(body.error || "Error").replaceAll("_", " "));
  return body;
}
const post = (path: string, body: any = {}) =>
  api(path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });

export function StocktakeWorkspace() {
  const [data, setData] = useState<any>(null);
  const [session, setSession] = useState<any>(null); // active session
  const [counts, setCounts] = useState<any[]>([]);
  const [step, setStep] = useState<"scan" | "confirm" | "count">("scan");
  const [scanInput, setScanInput] = useState("");
  const [scanResult, setScanResult] = useState<any>(null);
  const [selectedPart, setSelectedPart] = useState<any>(null);
  const [countInput, setCountInput] = useState("");
  const [lastCount, setLastCount] = useState<any>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const scanRef = useRef<HTMLInputElement>(null);

  async function load() {
    const d = await api("/api/cmms/stocktake");
    setData(d);
    if (d.mySession) {
      setSession(d.mySession);
      setCounts(d.myCounts || []);
    }
  }

  useEffect(() => { load(); }, []);
  useEffect(() => { if (step === "scan") scanRef.current?.focus(); }, [step]);

  async function startSession() {
    setBusy(true);
    try {
      const r = await post("/api/cmms/stocktake");
      await load();
      setStep("scan");
      setMessage("");
    } catch (e: any) { setMessage(e.message); }
    finally { setBusy(false); }
  }

  async function handleScan(e: React.FormEvent) {
    e.preventDefault();
    if (!scanInput.trim()) return;
    setBusy(true); setMessage("");
    try {
      const r = await api(`/api/cmms/stocktake/${session.id}/scan?q=${encodeURIComponent(scanInput.trim())}`);
      setScanResult(r);
      setScanInput("");
      if (r.parts.length === 1) {
        setSelectedPart(r.parts[0]);
        setStep("count");
        setCountInput(String(r.parts[0].system_qty));
      } else {
        setStep("confirm");
      }
    } catch (e: any) { setMessage(e.message); setScanInput(""); }
    finally { setBusy(false); }
  }

  async function recordCount(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedPart) return;
    const qty = Number(countInput);
    if (!Number.isFinite(qty) || qty < 0) return;
    setBusy(true);
    try {
      const r = await post(`/api/cmms/stocktake/${session.id}/counts`, {
        partId: selectedPart.id,
        countedQty: qty,
      });
      setLastCount({ part: selectedPart, ...r });
      await load();
      setStep("scan");
      setSelectedPart(null);
      setScanResult(null);
      setCountInput("");
    } catch (e: any) { setMessage(e.message); }
    finally { setBusy(false); }
  }

  async function commit() {
    if (!confirm(`Commit this stocktake? ${counts.filter(c => Number(c.variance) !== 0).length} adjustment(s) will be posted to inventory.`)) return;
    setBusy(true);
    try {
      const r = await post(`/api/cmms/stocktake/${session.id}/commit`);
      setSession(null); setCounts([]); setStep("scan"); setLastCount(null);
      setMessage(`Stocktake committed — ${r.adjustments} stock adjustment(s) posted.`);
      await load();
    } catch (e: any) { setMessage(e.message); }
    finally { setBusy(false); }
  }

  async function abandon() {
    if (!confirm("Abandon this stocktake? No adjustments will be posted and counts will be discarded.")) return;
    await post(`/api/cmms/stocktake/${session.id}/abandon`);
    setSession(null); setCounts([]); setStep("scan"); setLastCount(null);
    setMessage("Stocktake abandoned.");
    await load();
  }

  const variantCount = counts.filter(c => Number(c.variance) !== 0).length;
  const matchCount = counts.filter(c => Number(c.variance) === 0).length;

  if (!data) return <p className="func-loading">Loading…</p>;

  // No active session — show start screen + other open sessions
  if (!session) return (
    <>
      <div className="func-heading">
        <div><h1>Stocktake</h1><p>Scan bins and verify stock levels. Multiple people can count different sections simultaneously.</p></div>
        <button onClick={startSession} disabled={busy}><ScanLine /> Start stocktake</button>
      </div>
      {message && <div className="func-admin-note">{message}</div>}
      {(data.sessions || []).filter((s: any) => s.status === "OPEN").length > 0 && (
        <section className="func-panel">
          <div className="func-table-head"><h2>Active sessions</h2></div>
          <div className="func-list" style={{"--cols": "1fr 1fr 1fr"} as any}>
            <div className="func-list-row func-th"><span>Started by</span><span>Bins counted</span><span>Started</span></div>
            {data.sessions.filter((s: any) => s.status === "OPEN").map((s: any) => (
              <div className="func-list-row" key={s.id}>
                <span>{s.started_by_name}</span>
                <span>{s.bin_count} bins</span>
                <span>{new Date(s.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );

  return (
    <>
      <div className="func-heading">
        <div>
          <h1>Stocktake in progress</h1>
          <p>{counts.length} bin{counts.length !== 1 ? "s" : ""} counted · <span style={{color: variantCount > 0 ? "var(--red)" : "var(--green)"}}>{variantCount} variance{variantCount !== 1 ? "s" : ""}</span> · {matchCount} match{matchCount !== 1 ? "es" : ""}</p>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button className="secondary" onClick={abandon}>Abandon</button>
          <button onClick={commit} disabled={busy || counts.length === 0}>Commit stocktake</button>
        </div>
      </div>
      {message && <div className="func-admin-note">{message}</div>}

      {/* Scan step */}
      {step === "scan" && (
        <section className="func-panel stocktake-scan">
          <div className="func-table-head"><h2><ScanLine size={16}/> Scan or search</h2></div>
          {lastCount && (
            <div className={`stocktake-last ${Number(lastCount.variance) === 0 ? "match" : "variance"}`}>
              {Number(lastCount.variance) === 0
                ? <><CheckCircle size={16}/> {lastCount.part.canonical_name} — stock correct ({lastCount.countedQty})</>
                : <><XCircle size={16}/> {lastCount.part.canonical_name} — variance {Number(lastCount.variance) > 0 ? "+" : ""}{lastCount.variance} (system: {lastCount.systemQty} → counted: {lastCount.countedQty})</>
              }
            </div>
          )}
          <form className="stocktake-scan-form" onSubmit={handleScan}>
            <input
              ref={scanRef}
              autoFocus
              placeholder="Scan QR code or enter bin location / part code…"
              value={scanInput}
              onChange={e => setScanInput(e.target.value)}
              disabled={busy}
            />
            <button disabled={busy || !scanInput.trim()}>Look up</button>
          </form>
          <p className="stocktake-hint">Point your camera at a part QR label, or type a bin location (e.g. A-01-03) or part code.</p>
        </section>
      )}

      {/* Multiple parts matched — pick one */}
      {step === "confirm" && scanResult && (
        <section className="func-panel stocktake-scan">
          <div className="func-table-head">
            <h2>Multiple parts found — select one</h2>
            <button className="secondary" onClick={() => { setStep("scan"); setScanResult(null); }}>Back</button>
          </div>
          {scanResult.parts.map((p: any) => (
            <div key={p.id} className="stocktake-part-option" onClick={() => { setSelectedPart(p); setCountInput(String(p.system_qty)); setStep("count"); }}>
              <span>
                <b>{p.canonical_name}</b>
                <small>{p.primary_code || "No code"} · Bin: {p.bin_location || "No bin"}</small>
              </span>
              <em>{Number(p.system_qty)} in stock</em>
              {p.alreadyCounted && <span className="stocktake-counted-badge">Already counted by {p.alreadyCounted.started_by_name}</span>}
            </div>
          ))}
        </section>
      )}

      {/* Count entry */}
      {step === "count" && selectedPart && (
        <section className="func-panel stocktake-scan">
          <div className="func-table-head">
            <h2><PackageSearch size={16}/> Count — {selectedPart.canonical_name}</h2>
            <button className="secondary" onClick={() => { setStep("scan"); setSelectedPart(null); }}>Back</button>
          </div>
          <div className="stocktake-part-detail">
            <dl>
              <div><dt>Primary code</dt><dd>{selectedPart.primary_code || "—"}</dd></div>
              <div><dt>Bin location</dt><dd>{selectedPart.bin_location || "—"}</dd></div>
              <div><dt>Manufacturer</dt><dd>{selectedPart.manufacturer || "—"}</dd></div>
              <div><dt>System stock</dt><dd><b>{Number(selectedPart.system_qty)}</b></dd></div>
            </dl>
            {selectedPart.alreadyCounted && (
              <div className="stocktake-warning">
                ⚠️ Already counted in another session by {selectedPart.alreadyCounted.started_by_name}
              </div>
            )}
          </div>
          <form className="stocktake-count-form" onSubmit={recordCount}>
            <label>
              Physical count
              <input
                autoFocus
                type="number"
                min="0"
                step="0.001"
                value={countInput}
                onChange={e => setCountInput(e.target.value)}
                disabled={busy}
              />
            </label>
            {countInput !== "" && Number.isFinite(Number(countInput)) && (
              <div className={`stocktake-variance ${Number(countInput) === Number(selectedPart.system_qty) ? "match" : "variance"}`}>
                {Number(countInput) === Number(selectedPart.system_qty)
                  ? "✓ Matches system stock"
                  : `Variance: ${Number(countInput) - Number(selectedPart.system_qty) > 0 ? "+" : ""}${(Number(countInput) - Number(selectedPart.system_qty)).toFixed(3).replace(/\.?0+$/, "")}`
                }
              </div>
            )}
            <div style={{display:"flex",gap:8,marginTop:12}}>
              <button disabled={busy || countInput === ""}>Record count</button>
              <button type="button" className="secondary" onClick={() => { setStep("scan"); setSelectedPart(null); }}>Skip</button>
            </div>
          </form>
        </section>
      )}

      {/* Counts so far */}
      {counts.length > 0 && (
        <section className="func-panel" style={{marginTop:16}}>
          <div className="func-table-head"><h2>Counts recorded this session</h2></div>
          <div className="func-list" style={{"--cols":"2fr 1fr 1fr 1fr 1fr"} as any}>
            <div className="func-list-row func-th"><span>Part</span><span>Code</span><span>Bin</span><span>System</span><span>Counted / Variance</span></div>
            {counts.map((c: any) => (
              <div className="func-list-row" key={c.id}>
                <span>{c.canonical_name}</span>
                <span>{c.primary_code || "—"}</span>
                <span>{c.bin_location || "—"}</span>
                <span>{Number(c.system_qty)}</span>
                <span style={{color: Number(c.variance) !== 0 ? "var(--red)" : "var(--green)", fontWeight: 700}}>
                  {Number(c.counted_qty)}
                  {Number(c.variance) !== 0 && ` (${Number(c.variance) > 0 ? "+" : ""}${Number(c.variance)})`}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
